import { fmtN, fmtP, PaceArrow, PaceBadge } from "@/components/overview/Momentum";
import { TH, StageCells, GroupHead, SubHead } from "@/components/overview/StageCells";
import { fmtL, fmtRs } from "./SvcBits";

const SEP = "border-l-2 border-border/80";
const rs = (v) => (v == null ? "—" : `₹${fmtN(v)}`);

function Head({ period, first, group, tail, prefix }) {
  return (
    <thead className="border-b sticky top-0 bg-card z-[2]">
      <tr>
        <th className={`${TH} text-left sticky left-0 bg-card z-[3]`}>{first}</th>
        <GroupHead label={group} testId={`${prefix}-group`} />
        {tail.map((t, i) => <th key={t} className={`${TH} ${i === 0 ? SEP : ""} ${t === "5-day" || t === "DoD" || t === "Pace" ? "text-center" : "text-right"}`}>{t}</th>)}
      </tr>
      <tr className="text-[9px] text-muted-foreground">
        <th className="sticky left-0 bg-card" />
        <SubHead period={period} />
        {tail.map((t) => <th key={t} />)}
      </tr>
    </thead>
  );
}

// D · RO categories: ROs vs target, revenue, ₹/RO vs the group best, labour / parts per RO, rank (workshop scope), pace.
export function CategoryTable({ rows, period, scopeKind }) {
  const pace = period?.kind === "week" ? "DoD" : "5-day";
  const tail = ["Revenue", "₹ / RO", "Best ₹ / RO", "Labour / RO", "Parts / RO", ...(scopeKind === "workshop" ? ["Rank"] : []), pace];
  return (
    <div className="border bg-card rounded-md" data-testid="sov-category-table">
      <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">D · RO categories <span className="text-muted-foreground font-normal text-xs">· ROs {period?.kind === "week" ? "Week Target / WTD Target" : "Target / MTD Target"} / Ach / % · revenue and ₹ per RO per category · best = group-best workshop</span></h3></div>
      <div className="overflow-auto max-h-[420px]">
        <table className="w-full text-xs min-w-[980px]">
          <Head period={period} first="Category" group="ROs" tail={tail} prefix="sov-cat" />
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={`border-b last:border-0 hover:bg-muted/50 ${r.key === "GS" || r.key === "ALL" ? "bg-secondary/60 font-semibold" : "bg-card"}`} data-testid={`sov-cat-${r.key}`}>
                <td className="px-2 py-1 font-medium whitespace-nowrap sticky left-0 bg-inherit">{r.label}</td>
                <StageCells c={r.ros} period={period} testPrefix={`sov-cat-${r.key}-ros`} />
                <td className={`px-2 py-1 text-right num ${SEP}`} data-testid={`sov-cat-${r.key}-rev`}>{fmtRs(r.revenue)}</td>
                <td className="px-2 py-1 text-right num font-bold" data-testid={`sov-cat-${r.key}-perro`}>{rs(r.rev_per_ro)}</td>
                <td className="px-2 py-1 text-right num text-muted-foreground whitespace-nowrap">{rs(r.best_per_ro)}{r.best_code && <span className="text-[10px]"> · {r.best_code}</span>}</td>
                <td className="px-2 py-1 text-right num">{rs(r.labour_per_ro)}</td>
                <td className="px-2 py-1 text-right num">{rs(r.parts_per_ro)}</td>
                {scopeKind === "workshop" && <td className="px-2 py-1 text-right num" data-testid={`sov-cat-${r.key}-rank`}>{r.rank ?? "—"}</td>}
                <td className="px-2 py-1 text-center"><PaceArrow m={r.momentum} compact /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// E · Revenue classes: ₹ vs target, ₹ per GS RO vs best, penetration (share of GS ROs carrying the class), mix, pace.
export function ClassTable({ rows, period }) {
  const pace = period?.kind === "week" ? "DoD" : "5-day";
  const tail = ["₹ / GS RO", "Best ₹ / RO", "Penetration", "Mix target / ach", pace, "Pace"];
  return (
    <div className="border bg-card rounded-md" data-testid="sov-class-table">
      <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">E · Revenue classes <span className="text-muted-foreground font-normal text-xs">· {period?.kind === "week" ? "Week Target / WTD Target" : "Target / MTD Target"} / Ach / % · per GS RO · penetration = GS ROs with the class ÷ GS ROs</span></h3></div>
      <div className="overflow-auto max-h-[420px]">
        <table className="w-full text-xs min-w-[940px]">
          <Head period={period} first="Class" group="Revenue ₹" tail={tail} prefix="sov-cls" />
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={`border-b last:border-0 hover:bg-muted/50 ${r.key === "total_gs" ? "bg-secondary/60 font-semibold" : r.key === "warranty" ? "bg-emerald-50/40" : "bg-card"}`} data-testid={`sov-cls-${r.key}`}>
                <td className="px-2 py-1 font-medium whitespace-nowrap sticky left-0 bg-inherit">{r.label}</td>
                <StageCells c={r.cell} period={period} fmt={fmtL} testPrefix={`sov-cls-${r.key}`} />
                <td className={`px-2 py-1 text-right num font-bold ${SEP}`} data-testid={`sov-cls-${r.key}-perro`}>{rs(r.per_ro)}</td>
                <td className="px-2 py-1 text-right num text-muted-foreground">{rs(r.best_per_ro)}</td>
                <td className="px-2 py-1 text-right num" data-testid={`sov-cls-${r.key}-pen`}>{r.penetration == null ? "—" : fmtP(r.penetration)}</td>
                <td className="px-2 py-1 text-right num text-muted-foreground whitespace-nowrap">{fmtP(r.mix_plan)} / <span className="text-foreground">{fmtP(r.mix_ach)}</span></td>
                <td className="px-2 py-1 text-center"><PaceArrow m={r.momentum} compact /></td>
                <td className="px-2 py-1 text-center">{(!period || period.kind !== "week" || period.current) && r.cell?.plan != null && <PaceBadge p={r.pace_vs_plan} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
